import {
  Alert,
  Button,
  Checkbox,
  Empty,
  Flex,
  List,
  Modal,
  Radio,
  Select,
  Tag,
  Typography
} from "antd"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import type { WidgetInstanceV4 } from "../../DashboardPage/redux/widgets.types"
import type { TemplateFormValues } from "../helpers/payload"
import type { DashboardTemplate, TemplateRole, TemplateScope } from "../types"
import { TextInput } from "../../../components/Input/TextInput.tsx"

export interface AvailableWidget {
  instance: WidgetInstanceV4
  title: string
}

interface TemplateEditorProps {
  open: boolean
  editing: DashboardTemplate | null
  availableWidgets: AvailableWidget[]
  restrictedTitles: Map<string, string>
  catalogLoading: boolean
  roles: TemplateRole[]
  saving: boolean
  onAudienceChange: (role: number | null) => void
  onCancel: () => void
  onSubmit: (values: TemplateFormValues) => void
}

const emptyForm = (): {
  scope: TemplateScope
  role: number | null
  key: string
  name: string
  widgets: WidgetInstanceV4[]
  isDefault: boolean
} => ({
  scope: "company",
  role: null,
  key: "",
  name: "",
  widgets: [],
  isDefault: false
})

export const TemplateEditor = ({
  open,
  editing,
  availableWidgets,
  restrictedTitles,
  catalogLoading,
  roles,
  saving,
  onAudienceChange,
  onCancel,
  onSubmit
}: TemplateEditorProps) => {
  const { t } = useTranslation()
  const [scope, setScope] = useState<TemplateScope>("company")
  const [role, setRole] = useState<number | null>(null)
  const [key, setKey] = useState("")
  const [name, setName] = useState("")
  const [widgets, setWidgets] = useState<WidgetInstanceV4[]>([])
  const [isDefault, setIsDefault] = useState(false)

  useEffect(() => {
    if (!open) return
    if (editing) {
      setScope(editing.scope)
      setRole(editing.role)
      setKey(editing.key)
      setName(editing.layout.name)
      setWidgets(editing.layout.widgets)
      setIsDefault(!!editing.is_default)
    } else {
      const blank = emptyForm()
      setScope(blank.scope)
      setRole(blank.role)
      setKey(blank.key)
      setName(blank.name)
      setWidgets(blank.widgets)
      setIsDefault(blank.isDefault)
    }
  }, [open, editing])

  const audience = scope === "role" ? role : null
  useEffect(() => {
    if (open) onAudienceChange(audience)
  }, [open, audience, onAudienceChange])

  const selectedSlugs = useMemo(
    () => new Set(widgets.map((w) => w.slug)),
    [widgets]
  )
  const reachableSlugs = useMemo(
    () => new Set(availableWidgets.map((w) => w.instance.slug)),
    [availableWidgets]
  )
  const addable = availableWidgets.filter(
    (w) => !selectedSlugs.has(w.instance.slug)
  )
  const titleForSlug = useMemo(() => {
    const map = new Map<string, string>()
    for (const w of availableWidgets) map.set(w.instance.slug, w.title)
    for (const [slug, title] of restrictedTitles)
      if (!map.has(slug)) map.set(slug, title)
    return map
  }, [availableWidgets, restrictedTitles])

  // The resolution filter drops these for every holder of the role, and a
  // template it empties falls back to the default board — so the save is
  // refused rather than silently reaching nobody.
  const unreachable = catalogLoading
    ? []
    : widgets.filter((w) => !reachableSlugs.has(w.slug)).map((w) => w.slug)
  const unreachableSlugs = new Set(unreachable)

  const addWidget = (instance: WidgetInstanceV4) =>
    setWidgets((prev) =>
      prev.some((w) => w.slug === instance.slug) ? prev : [...prev, instance]
    )

  const removeWidget = (slug: string) =>
    setWidgets((prev) => prev.filter((w) => w.slug !== slug))

  const move = (index: number, delta: number) =>
    setWidgets((prev) => {
      const next = [...prev]
      const target = index + delta
      if (target < 0 || target >= next.length) return prev
      ;[next[index], next[target]] = [next[target], next[index]]
      return next
    })

  const canSubmit =
    key.trim().length > 0 &&
    name.trim().length > 0 &&
    (scope === "company" || (scope === "role" && !!role)) &&
    unreachable.length === 0 &&
    widgets.length > 0

  const handleOk = () => {
    if (!canSubmit) return
    onSubmit({ scope, role, key, name, widgets, isDefault })
  }

  return (
    <Modal
      open={open}
      title={
        editing
          ? t("settingsDashboardTemplates.editor.editTitle", "Edit template")
          : t("settingsDashboardTemplates.editor.createTitle", "New template")
      }
      okText={t("settingsDashboardTemplates.editor.save", "Save")}
      cancelText={t("settingsDashboardTemplates.editor.cancel", "Cancel")}
      confirmLoading={saving}
      okButtonProps={{ disabled: !canSubmit }}
      onOk={handleOk}
      onCancel={onCancel}
      destroyOnClose
      width={640}
    >
      <Flex vertical gap={12}>
        <div>
          <Typography.Text strong>
            {t("settingsDashboardTemplates.editor.scope", "Scope")}
          </Typography.Text>
          <div>
            <Radio.Group
              value={scope}
              onChange={(e) => setScope(e.target.value as TemplateScope)}
            >
              <Radio.Button value="company">
                {t("settingsDashboardTemplates.scope.company", "Company")}
              </Radio.Button>
              <Radio.Button value="role">
                {t("settingsDashboardTemplates.scope.role", "Role")}
              </Radio.Button>
            </Radio.Group>
          </div>
        </div>

        {scope === "role" && (
          <div>
            <Typography.Text strong>
              {t("settingsDashboardTemplates.editor.role", "Role")}
            </Typography.Text>
            <Select
              value={role ?? undefined}
              onChange={setRole}
              style={{ width: "100%" }}
              placeholder={t(
                "settingsDashboardTemplates.editor.rolePlaceholder",
                "Select a role"
              )}
              options={roles.map((r) => ({ value: r.id, label: r.name }))}
              aria-label={t("settingsDashboardTemplates.editor.role", "Role")}
            />
          </div>
        )}

        <div>
          <Typography.Text strong>
            {t("settingsDashboardTemplates.editor.key", "Key")}
          </Typography.Text>
          <TextInput
            disableAutoCapitalize
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder={t(
              "settingsDashboardTemplates.editor.keyPlaceholder",
              "unique_key"
            )}
            aria-label={t("settingsDashboardTemplates.editor.key", "Key")}
          />
        </div>

        <div>
          <Typography.Text strong>
            {t("settingsDashboardTemplates.editor.name", "Name")}
          </Typography.Text>
          <TextInput
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t(
              "settingsDashboardTemplates.editor.namePlaceholder",
              "Display name"
            )}
            aria-label={t("settingsDashboardTemplates.editor.name", "Name")}
          />
        </div>

        <div>
          <Checkbox
            checked={isDefault}
            onChange={(e) => setIsDefault(e.target.checked)}
            aria-label={t(
              "settingsDashboardTemplates.editor.isDefault",
              "Use as the default"
            )}
          >
            {t(
              "settingsDashboardTemplates.editor.isDefault",
              "Use as the default"
            )}
          </Checkbox>
          <div>
            <Typography.Text type="secondary">
              {t(
                "settingsDashboardTemplates.editor.isDefaultHint",
                "Applied when more than one template targets this audience."
              )}
            </Typography.Text>
          </div>
        </div>

        <div>
          <Typography.Text strong>
            {t("settingsDashboardTemplates.editor.layout", "Layout")}
          </Typography.Text>
          {unreachable.length > 0 && (
            <Alert
              type="warning"
              showIcon
              className="mb-2"
              message={t(
                "settingsDashboardTemplates.editor.unreachableTitle",
                "Some widgets are not available to this role"
              )}
              description={t(
                "settingsDashboardTemplates.editor.unreachableBody",
                "This role has no access to them, so they would not appear on its dashboard. Remove them to save the template."
              )}
            />
          )}
          {widgets.length === 0 && (
            <Alert
              type="info"
              showIcon
              className="mb-2"
              message={t(
                "settingsDashboardTemplates.editor.emptyTitle",
                "A template needs at least one widget"
              )}
              description={t(
                "settingsDashboardTemplates.editor.emptyBody",
                "An empty template would give everyone it applies to a blank dashboard."
              )}
            />
          )}
          <List
            size="small"
            bordered
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={t(
                    "settingsDashboardTemplates.editor.noWidgets",
                    "No widgets yet"
                  )}
                />
              )
            }}
            dataSource={widgets}
            renderItem={(w, index) => (
              <List.Item
                actions={[
                  <Button
                    key="up"
                    size="small"
                    type="text"
                    disabled={index === 0}
                    aria-label={t(
                      "settingsDashboardTemplates.editor.moveUp",
                      "Move up"
                    )}
                    onClick={() => move(index, -1)}
                  >
                    ↑
                  </Button>,
                  <Button
                    key="down"
                    size="small"
                    type="text"
                    disabled={index === widgets.length - 1}
                    aria-label={t(
                      "settingsDashboardTemplates.editor.moveDown",
                      "Move down"
                    )}
                    onClick={() => move(index, 1)}
                  >
                    ↓
                  </Button>,
                  <Button
                    key="remove"
                    size="small"
                    type="text"
                    danger
                    aria-label={t(
                      "settingsDashboardTemplates.editor.remove",
                      "Remove"
                    )}
                    onClick={() => removeWidget(w.slug)}
                  >
                    ×
                  </Button>
                ]}
              >
                <Flex align="center" gap={8}>
                  <Typography.Text
                    type={unreachableSlugs.has(w.slug) ? "danger" : undefined}
                  >
                    {titleForSlug.get(w.slug) ?? w.slug}
                  </Typography.Text>
                  {unreachableSlugs.has(w.slug) && (
                    <Tag color="warning">
                      {t(
                        "settingsDashboardTemplates.editor.noRoleAccess",
                        "No access for this role"
                      )}
                    </Tag>
                  )}
                </Flex>
              </List.Item>
            )}
          />
        </div>

        <div>
          <Select
            style={{ width: "100%" }}
            value={null}
            placeholder={t(
              "settingsDashboardTemplates.editor.addWidget",
              "Add a widget"
            )}
            loading={catalogLoading}
            disabled={catalogLoading || addable.length === 0}
            aria-label={t(
              "settingsDashboardTemplates.editor.addWidget",
              "Add a widget"
            )}
            onChange={(slug: string) => {
              const found = addable.find((w) => w.instance.slug === slug)
              if (found) addWidget(found.instance)
            }}
            options={addable.map((w) => ({
              value: w.instance.slug,
              label: w.title
            }))}
          />
        </div>
      </Flex>
    </Modal>
  )
}
